import { BridgeMessage, MessageType } from "@shared/services/webview/types";
import * as Sentry from "@sentry/react";

export const SECURITY_CONFIG = {
  MAX_PAYLOAD_SIZE: 30000,
  MAX_TEXT_MESSAGE_SIZE: 2000,
};

export const validateMessage = (message: any): message is BridgeMessage => {
  if (!message || typeof message !== "object") {
    Sentry.captureMessage(
      `WebView Security: Message is not a valid object: ${message}}`,
      "warning"
    );
    return false;
  }

  if (!message.type || typeof message.type !== "string") {
    Sentry.captureMessage(
      `"WebView Security: Message missing or invalid type field": ${message}}`,
      "warning"
    );
    return false;
  }

  if (!Object.values(MessageType).includes(message.type)) {
    Sentry.captureMessage(
      `WebView Security: Unknown message type: ${message.type}`,
      "warning"
    );
    return false;
  }

  if (message.payload) {
    const payloadSize = JSON.stringify(message.payload).length;
    if (payloadSize > SECURITY_CONFIG.MAX_PAYLOAD_SIZE) {
      Sentry.captureMessage(
        `WebView Security: Payload too large: ${payloadSize} characters, message: ${message}`,
        "warning"
      );
      return false;
    }
  }

  return true;
};

export const sanitizeString = (input: any): string => {
  if (typeof input !== "string") return "";

  return input
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#x27;")
    .replace(/\//g, "&#x2F;");
};

export const validateNavigationRequest = (pathname: any): boolean => {
  if (typeof pathname !== "string") {
    Sentry.captureMessage(
      "WebView Security: Navigation pathname is not a string",
      "warning"
    );
    return false;
  }

  if (pathname.startsWith("http://") || pathname.startsWith("https://")) {
    Sentry.captureMessage(
      `WebView Security: External navigation blocked: ${pathname}`,
      "warning"
    );
    return false;
  }

  const dangerousProtocols = ["javascript:", "data:", "vbscript:", "file:"];
  if (
    dangerousProtocols.some((protocol) =>
      pathname.toLowerCase().startsWith(protocol)
    )
  ) {
    Sentry.captureMessage(
      `WebView Security: Dangerous protocol navigation blocked: ${pathname}`,
      "warning"
    );
    return false;
  }

  if (!pathname.startsWith("/")) {
    Sentry.captureMessage(
      `WebView Security: Invalid pathname format: ${pathname}`,
      "warning"
    );
    return false;
  }

  return true;
};

export const logSecurityEvent = (
  event: string,
  details?: Record<string, string>
): void => {
  Sentry.captureMessage(`WebView Security: ${event}`, {
    extra: { payload: details },
    level: "warning",
  });
};
